// File: src/components/CalendarView/DetailedCalendarView.js
import React, { useState } from 'react';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { GradientButton } from '../ui/GradientButton';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import CalendarEventItem from './CalendarEventItem';

const DetailedCalendarView = ({ onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { 
    events, 
    loading, 
    error, 
    isAuthenticated, 
    signIn, 
    signOut 
  } = useGoogleCalendar();

  // Function to render the calendar header with month/year and navigation
  const header = () => {
    const dateFormat = "MMMM yyyy";
    
    const prevMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    };

    const nextMonth = () => {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    };

    return (
      <div className="flex items-center justify-between p-4">
        <GradientButton
          onClick={prevMonth}
          className="!px-3"
        >
          <ChevronLeft className="w-4 h-4" />
        </GradientButton>
        <h2 className="text-xl font-semibold">
          {format(currentDate, dateFormat)}
        </h2>
        <GradientButton
          onClick={nextMonth}
          className="!px-3"
        >
          <ChevronRight className="w-4 h-4" />
        </GradientButton>
      </div>
    );
  };

  // Function to render the days of the week header
  const daysOfWeek = () => {
    const dateFormat = "EEE";
    const days = [];
    let startDate = startOfWeek(currentDate);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="p-4 text-center font-semibold text-gray-600">
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }
    return <div className="grid grid-cols-7 border-b">{days}</div>;
  };

  // Function to get events for a specific day
  const getEventsForDay = (day) => {
    if (!events) return [];
    
    return events.filter(event => {
      const eventDate = event.start?.dateTime 
        ? parseISO(event.start.dateTime)
        : event.start?.date 
          ? parseISO(event.start.date)
          : null;
          
      if (!eventDate) return false;
      return isSameDay(eventDate, day);
    });
  };

  // Function to render the calendar cells
  const cells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfMonth(monthEnd);

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayEvents = getEventsForDay(cloneDay);
        const formattedDate = format(day, dateFormat);

        days.push(
          <div
            key={day.toString()}
            className={`min-h-[120px] p-2 border ${
              !isSameMonth(day, monthStart)
                ? "bg-gray-50 text-gray-400"
                : isSameDay(day, selectedDate)
                ? "bg-violet-50"
                : ""
            } hover:bg-violet-50/50 transition-colors cursor-pointer`}
            onClick={() => {
              setSelectedDate(cloneDay);
              onDateSelect && onDateSelect(cloneDay);
            }}
          >
            <div className="flex justify-between items-center mb-1">
              <span className={`text-sm font-medium ${
                isSameDay(day, new Date()) ? 'bg-violet-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''
              }`}>
                {formattedDate}
              </span>
              {dayEvents.length > 0 && (
                <span className="text-xs bg-violet-200 text-violet-700 px-1.5 rounded-full">
                  {dayEvents.length}
                </span>
              )}
            </div>
            <div className="space-y-1 overflow-y-auto max-h-[80px] scrollbar-thin scrollbar-thumb-violet-200 scrollbar-track-transparent">
              {dayEvents.map((event, idx) => (
                <CalendarEventItem key={event.id || idx} event={event} />
              ))}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  // Main render
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex justify-between items-center p-4 border-b">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-semibold">Calendar</h2>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-violet-600" />}
        </div>
        <div className="flex items-center gap-2">
          {error && <p className="text-sm text-red-500 mr-2">{error}</p>}
          <GradientButton
            onClick={isAuthenticated ? signOut : signIn}
            className="flex items-center gap-2"
          >
            {isAuthenticated ? "Disconnect Calendar" : "Connect Google Calendar"}
          </GradientButton>
        </div>
      </div>
      <div className="calendar">
        {header()}
        {daysOfWeek()}
        {cells()}
      </div>
    </div>
  );
};

export default DetailedCalendarView;